import './VideoPermissionsHelperModal.scss';
import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { FormattedText } from 'core/i18n';
import { ModalComponent } from 'components/modal/modalComponent';
import { recordEventCustomOnly } from 'utilities/uTagging';
import { VIDEO_SELFIE } from 'constants/identityMethod';
import { OS_MAP, BROWSER_MAP, isXiaomiDevice, isHuawei, CUSTOM_BROWSER_MAP, CUSTOM_OS_MAP } from 'utilities/browser';

export const VideoPermissionsHelperModal = ({
    showModal,
    onCancel,
    onConfirm,
    osName,
    browser,
    pictureName,
    picture,
    isMobile,
    videoType,
    storage,
    isIbanActive,
}) => {
    const getDesktop = () => {
        const { safari, chrome, edge, firefox } = BROWSER_MAP;

        const desktop = {
            [edge]: <FormattedText value={'identification-chromeAndEdgeDesktopPermissionIndicator'} />,
            [chrome]: <FormattedText value={'identification-chromeAndEdgeDesktopPermissionIndicator'} />,
            [firefox]: <FormattedText value={'identification-firefoxPermissionIndicator'} />,
            [safari]: <FormattedText value={'identification-safariPermissionIndicator'} />,
            other: <FormattedText value={'identification-otherPermissionIndicator'} />,
        };

        return desktop[browser] || desktop.other;
    };

    const getMobile = () => {
        const { ios, android } = OS_MAP;
        const { miui } = CUSTOM_OS_MAP;
        const { safari, chrome, edge, firefox, samsung } = BROWSER_MAP;
        const { huawei, samsung: samsungInternet } = CUSTOM_BROWSER_MAP;

        const mobile = {
            [ios]: {
                [safari]: safari,
                [chrome]: chrome,
                [edge]: edge,
                [firefox]: firefox,
                other: <FormattedText value={'identification-iOSOtherBrowserSettings'} />,
            },
            [android]: {
                [chrome]: chrome,
                [edge]: edge,
                [firefox]: firefox,
                [samsung]: samsungInternet,
                [huawei]: huawei,
                other: <FormattedText value={'identification-AndroidOtherBrowserSettings'} />,
            },
            [miui]: {
                [chrome]: chrome,
                [edge]: edge,
                [firefox]: firefox,
                other: <FormattedText value={'identification-MiuiOtherBrowserSettings'} />,
            },
            other: <FormattedText value={'identification-genericBrowserSettings'} />,
        };

        const currentOS = (isXiaomiDevice() && miui) || osName;
        const currentBrowser = (isHuawei() && huawei) || browser;

        if (!mobile[currentOS]) return mobile.other;
        if (!mobile[currentOS][currentBrowser]) return mobile[currentOS].other;

        return (
            <FormattedText
                value={`identification-${currentOS}NamedBrowserSettings`}
                injectedStrings={[mobile[currentOS][currentBrowser]]}
            />
        );
    };

    useEffect(() => {
        const identification = storage.get('identification');

        if (showModal) {
            window.scrollTo(0, 0);
            recordEventCustomOnly({
                categoriaEvento: 'onboarding/identidad/',
                accionEvento: `modal/videoselfie-permisos-instrucciones/${pictureName}`,
                etiquetaEvento: 'vista',
            });
        }

        if (!identification?.modalShown && showModal) {
            storage.set('identification', { ...identification, modalShown: true });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showModal]);

    return (
        <ModalComponent
            isOpen={showModal}
            className="modal-video-permissions-helper"
            cancelText={isIbanActive && <FormattedText value="identification-iPrefereTransfer" />}
            onCancel={onCancel}
            confirmText={<FormattedText value="identification-alreadyGranted" />}
            onConfirm={onConfirm}
        >
            <img
                src={picture}
                alt="video-permissions-helper"
                className={`modal-video-permissions-helper__img modal-video-permissions-helper__img--${
                    pictureName === 'other' ? 'other' : 'browser'
                }`}
            />
            <h1 className="modal-video-permissions-helper__text modal-video-permissions-helper__text--title">
                {videoType === VIDEO_SELFIE ? (
                    <FormattedText value={'identification-grantPermissionsVideoSelfie'} />
                ) : (
                    <FormattedText value={'identification-grantPermissionsVideoCall'} />
                )}
            </h1>
            <ol className="modal-video-permissions-helper__list">
                <li className="modal-video-permissions-helper__text modal-video-permissions-helper__text--subtitle">
                    {isMobile ? getMobile() : getDesktop()}
                </li>
                <li className="modal-video-permissions-helper__text modal-video-permissions-helper__text--subtitle">
                    <FormattedText value={'identification-clickOnAlreadyGranted'} />
                </li>
                <li className="modal-video-permissions-helper__text modal-video-permissions-helper__text--subtitle">
                    <FormattedText value={'identification-youWillReturnToTheMethodSelectionScreen'} />
                </li>
            </ol>
            {isIbanActive && (
                <p className="modal-video-permissions-helper__text modal-video-permissions-helper__text--additional-info">
                    <FormattedText value={'identification-youCanChooseTransfer'} />
                </p>
            )}
        </ModalComponent>
    );
};

VideoPermissionsHelperModal.propTypes = {
    showModal: PropTypes.bool,
    onCancel: PropTypes.func,
    onConfirm: PropTypes.func,
    osName: PropTypes.string,
    browser: PropTypes.string,
    picture: PropTypes.object,
    pictureName: PropTypes.string,
    isMobile: PropTypes.bool,
    videoType: PropTypes.string,
    storage: PropTypes.object,
    isIbanActive: PropTypes.bool,
};
